import { EmailContent } from "../../types";
import { htmlToText } from "../../utils/textProcessing";
import { logger } from "../../utils/logger";

export function getEmailBody(coercionType: Office.CoercionType = Office.CoercionType.Text): Promise<string> {
  return new Promise((resolve, reject) => {
    logger.debug("Getting email body...");
    const item = Office.context.mailbox.item;
    
    if (item && item.body && typeof item.body.getAsync === "function") {
      item.body.getAsync(coercionType, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value);
        } else {
          reject(result.error?.message || "Failed to get email body.");
        }
      });
    } else {
      reject("Item body is not available in this context.");
    }
  });
}

export function insertEmailBody(
  content: string, 
  coercionType: Office.CoercionType = Office.CoercionType.Html
): Promise<void> {
  return new Promise((resolve, reject) => {
    const item = Office.context.mailbox.item;
    
    if (item && item.body && typeof item.body.setAsync === "function") {
      item.body.setAsync(
        content,
        { coercionType },
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve();
          } else {
            item.notificationMessages?.addAsync("insertError", {
              type: "errorMessage",
              message: "Failed to insert: " + (result.error?.message || "Unknown error")
            });
            reject(result.error?.message || "Failed to insert content.");
          }
        }
      );
    } else {
      reject("Cannot insert content in this context.");
    }
  });
}

export function getCurrentMailboxItem(): Office.MessageRead | Office.MessageCompose | null {
  return Office.context?.mailbox?.item || null;
}

export function displayReplyForm(item: Office.MessageRead, htmlContent: string): void {
  if (item && "displayReplyForm" in item) {
    item.displayReplyForm({ htmlBody: htmlContent });
  } else {
    throw new Error("Cannot display reply form in this context");
  }
}

export function splitHtmlReply(bodyHtml: string): EmailContent {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(bodyHtml, "text/html");

    // Look for Outlook reply separator
    const replyForwardDiv = doc.querySelector("#divRplyFwdMsg");
    
    if (replyForwardDiv) {
      const divHtml = replyForwardDiv.outerHTML;
      const parts = bodyHtml.split(divHtml);
      
      if (parts.length >= 2) {
        return {
          replyOnly: parts[0].trim(),
          emailOnly: divHtml + parts.slice(1).join(divHtml),
          hasOriginalEmail: true
        };
      }
    }

    // Fallbacks for other patterns
    const hr = doc.querySelector("hr");
    if (hr) {
      const hrHtml = hr.outerHTML;
      const parts = bodyHtml.split(hrHtml);
      
      if (parts.length >= 2) {
        return {
          replyOnly: parts[0].trim(),
          emailOnly: hrHtml + parts.slice(1).join(hrHtml),
          hasOriginalEmail: true
        };
      }
    }

    // Text-based detection of common patterns
    const patterns = [
      /-----Original Message-----/i,
      /------------------\s*Original\s*------------------/i,
      /From:\s*[^\n]+/i,
      /Sent:\s*[^\n]+/i
    ];
    
    for (const pattern of patterns) {
      const match = bodyHtml.match(pattern);
      if (match && match.index) {
        return {
          replyOnly: bodyHtml.substring(0, match.index).trim(),
          emailOnly: bodyHtml.substring(match.index).trim(),
          hasOriginalEmail: true
        };
      }
    }

    // Default behavior if no separator found
    return {
      replyOnly: bodyHtml,
      emailOnly: "",
      hasOriginalEmail: false
    };
  } catch (error) {
    logger.error("Error parsing HTML reply:", error);
    return {
      replyOnly: bodyHtml,
      emailOnly: "",
      hasOriginalEmail: false
    };
  }
}

// Extensible service for future Office applications
export const outlookService = {
  getEmailBody,
  insertEmailBody,
  getCurrentMailboxItem,
  displayReplyForm,
  splitHtmlReply,
  
  getUserInfo: () => ({
    displayName: Office.context?.mailbox?.userProfile?.displayName || "",
    emailAddress: Office.context?.mailbox?.userProfile?.emailAddress || ""
  }),
  
  getSenderName: (): string => {
    try {
      const item = Office.context?.mailbox?.item;
      if (item && "from" in item && item.from) {
        return item.from.displayName || "";
      }
    } catch (error) {
      logger.warn("Could not get sender name:", error);
    }
    return "";
  }
};